// resumeUploadJob.js
'use strict';
import getAssetData from '../selector/getAssetData.js';
import getListData from '../selector/getListData.js';
import getOperationData from '../selector/getOperationData.js';
import getAssetAverageProgress from '../selector/getAssetAverageProgress.js';

import processUploadJob from '../action/processUploadJob.js';
import subscribePresenceMessage from '../action/subscribePresenceMessage.js';
import setUploadJobStatus from '../action/setUploadJobStatus.js';
import subscribePresenceAsset from '../action/subscribePresenceAsset.js';

import { Status as UploadStatus } from '../resource/uploadConstants.js';
import { upload as uploadDebug } from '../resource/debug.js';
import { ArtifactType } from '../resource/assetConstants.js';

const uploadLog = uploadDebug.extend('log:action/resumeUploadJob.js');

/**
 * Resume upload queue
 * @kind action
 * @return {Promise} Action promise.
 */
const resumeUploadJob = () => async (dispatch, getState) => {
  uploadLog('execute resumeUploadJob', {
    uploadJobIds: getListData(getState(), ['uploadJob'], 'itemIds'),
    uploadJob: getOperationData(getState(), ['uploadJob']),
  });
  const uploadJobIds = getListData(getState(), ['uploadJob'], 'itemIds') || [];
  const toFailedTasks = uploadJobIds
    .filter(uploadJobId => {
      const status = getOperationData(
        getState(),
        ['uploadJob', uploadJobId],
        'status'
      );
      const assetIds = getOperationData(
        getState(),
        ['uploadJob', uploadJobId],
        'assetIds'
      );
      const progress = getAssetAverageProgress(getState(), assetIds);
      // `presence-asset@ASSET_ID` channel data not includes artifact events
      // Needs update upload job status to failed to avoid getting stuck in uploading status.
      const isAllThumbnailUploadCompleted = (assetIds || []).every(
        assetId =>
          UploadStatus.UPLOAD_COMPLETED ===
          getAssetData(getState(), [assetId, ArtifactType.THUMBNAIL], 'status')
      );
      if (
        UploadStatus.UPLOAD_STARTED === status &&
        progress === 100 &&
        isAllThumbnailUploadCompleted
      ) {
        return false;
      }
      return [
        UploadStatus.PENDING,
        UploadStatus.DELETING,
        UploadStatus.UPLOAD_STARTED,
        UploadStatus.UPLOAD_FAILED,
      ].includes(status);
    })
    .map(uploadJobId =>
      dispatch(
        setUploadJobStatus({
          uploadJobId,
          status: UploadStatus.FAILED,
          failedReason: 'uploader_uploading_refresh_page_error',
        })
      )
    );
  await Promise.all(toFailedTasks);
  const toSubscribeTasks = uploadJobIds
    .filter(uploadJobId => {
      const status = getOperationData(
        getState(),
        ['uploadJob', uploadJobId],
        'status'
      );
      return [
        UploadStatus.UPLOAD_STARTED,
        UploadStatus.UPLOAD_COMPLETED,
        UploadStatus.PROCESSING_STARTED,
        UploadStatus.PROCESSING_COMPLETED,
        UploadStatus.REVIEW_STARTED,
        UploadStatus.REVIEW_FAILED,
        UploadStatus.REVIEW_COMPLETED,
        UploadStatus.DELIVERY_STARTED,
        UploadStatus.DELIVERY_COMPLETED,
        UploadStatus.DELIVERY_FAILED,
      ].includes(status);
    })
    .map(uploadJobId => {
      const status = getOperationData(
        getState(),
        ['uploadJob', uploadJobId],
        'status'
      );
      if (UploadStatus.UPLOAD_STARTED === status) {
        const assetIds =
          getOperationData(
            getState(),
            ['uploadJob', uploadJobId],
            'assetIds'
          ) || [];
        return assetIds.forEach(assetId =>
          dispatch(
            subscribePresenceAsset({
              assetId,
              uploadJobId,
            })
          )
        );
      }
      const messageId = getOperationData(
        getState(),
        ['uploadJob', uploadJobId],
        'messageId'
      );
      if (messageId) {
        return dispatch(subscribePresenceMessage({ messageId, uploadJobId }));
      }
    });
  await Promise.all(toSubscribeTasks);
  dispatch(processUploadJob());
  return uploadLog('finish resumeUploadJob');
};

export default resumeUploadJob;
